import { useContext, useEffect } from 'react'

import AssociationContext from 'Root/App/Card/Content/AssociationContext'
import RelatedContentsContext from 'Root/App/Card/Content/RelatedContentsContext'
import ShareWrapper from 'Root/App/Card/Content/ShareWrapper'
import { getMenuItems } from 'Root/App/Card/Content/utilities'
import UsageContext from 'Root/App/Contexts/Usage'
import Context from 'Root/App/Menu/Context'
import mixpanelTrack from 'lib/mixpanel-track'
import { useCurrentUserId } from 'lib/use-current-user-id'
import { useIsClient } from 'lib/use-is-client'
import { userElementAuthorization } from 'lib/userElementAuthorization'
import { useRouter } from 'next/router'

import { Context as CollectionContext } from '../Collection/Component/Context'
import CommonType from './Type/Common/index'
import EventType from './Type/Event/index'
import ImageType from './Type/Image/index'
import Pdf from './Type/Pdf/index'
import Quiz from './Type/Quiz/index'
import TextType from './Type/Text/index'

// ----------------------------------------------------------------------------

const Contexts = ({ children, content }) => (
  <AssociationContext>
    {content.__typename === 'EventContent' ? (
      <RelatedContentsContext>{children}</RelatedContentsContext>
    ) : (
      children
    )}
  </AssociationContext>
)

const Content = ({
  ssContent,
  ssCollection,
}: {
  ssContent: any
  ssCollection: any
}) => {
  const { push } = useRouter()
  const userId = useCurrentUserId()
  const { updateCollectionData } = useContext(CollectionContext)

  useEffect(() => {
    if (collection) {
      updateCollectionData(collection)
    }

    if (!ssContent) {
      return
    }

    mixpanelTrack('Contenuto visitato', {
      nome_contenuto: ssContent.title,
      id_contenuto: ssContent.slug,
      autore: ssContent.creator.vanity_name,
      visibilita: ssContent.visibilityForCollection,
    })
  }, [])

  const isClient = useIsClient()

  if (!isClient) return null

  const content = {
    ...ssContent,
    collectionCanRead: ssCollection.canRead,
  }
  const userIsCreator = content.creator.id === userId

  const type = content?.__typename

  const items = getMenuItems(content, null, null)

  const isCommon =
    type !== 'EventContent' &&
    type !== 'ImageContent' &&
    type !== 'PdfContent' &&
    type !== 'QuizContent' &&
    type !== 'TextContent'

  const collection = ssCollection
  const visibility = collection.visibility
  const preparatory = collection.preparatory
  content.collectionCanRead = collection.canRead

  if (
    !userIsCreator &&
    !userElementAuthorization(
      collection.canKeepAccess,
      content.creator.subscriptionPlan.code
    )
  ) {
    mixpanelTrack(`Errore: Tentativo di visualizzazione contenuto`, {
      id_contenuto: content.slug,
      nome_contenuto: content.title,
      visibilita_contenuto: content.visibility,
      email_creator: content.creator.email,
      autore: content.creator.vanity_name,
    })

    push(
      {
        pathname: '/not-available',
        query: {
          identifier: content.creator.id,
        },
      },
      '/not-available'
    )
  }

  return (
    <ShareWrapper content={content}>
      <Contexts content={content}>
        <Context.Provider value={items}>
          <UsageContext useCase="page">
            {type === 'EventContent' && (
              <EventType
                content={content}
                collectionVisibility={visibility}
                collectionPreparatory={preparatory}
              />
            )}
            {type === 'ImageContent' && (
              <ImageType
                content={content}
                collectionVisibility={visibility}
                collectionPreparatory={preparatory}
              />
            )}
            {type === 'PdfContent' && (
              <Pdf
                content={content}
                collectionVisibility={visibility}
                collectionPreparatory={preparatory}
              />
            )}
            {type === 'QuizContent' && (
              <Quiz
                content={content}
                collectionVisibility={visibility}
                collectionPreparatory={preparatory}
              />
            )}
            {type === 'TextContent' && (
              <TextType
                content={content}
                collectionVisibility={visibility}
                collectionPreparatory={preparatory}
              />
            )}
            {isCommon && (
              <CommonType
                content={content}
                collectionVisibility={visibility}
                collectionPreparatory={preparatory}
              />
            )}
          </UsageContext>
        </Context.Provider>
      </Contexts>
    </ShareWrapper>
  )
}

export default Content
